import type * as lazyIndex from "./lazyIndex";
import type * as accountProofs from "./utils/initializeAccountProofs";
import { createLazyComponent, type LazyComponent, LazyImport, LazyModule, LazyAction, } from "owa-bundling";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getProtectYourAccountDialogStore } from "./store/store";

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(() => import(/* webpackChunkName: "OwaAccountProtectionDialog" */ "./lazyIndex"), { name: "OwaAccountProtectionDialog" });
const fullModuleImport = new LazyImport(lazyModule, m => m);

// Idle initialization only needs the proof-state check; the dialog loads when it is shown.
const initializationLazyModule: LazyModule<typeof accountProofs> = new LazyModule(() => isGlobalFeatureEnabled("perf-memory-defer-idle-ui", true /* dontThrowErrorIfNotInitialized */) &&
    !isGlobalFeatureEnabled("perf-memory-defer-account-proofs-ui-ripcord", true /* dontThrowErrorIfNotInitialized */)
    ? import(
    /* webpackChunkName: "OwaAccountProofsInitialization" */ "./utils/initializeAccountProofs")
    : fullModuleImport.import(), { name: "OwaAccountProofsInitialization" });

/**
 * Entry point that wires up the "Protect your account" dialog: it subscribes to consumer account
 * startup, runs the MSA proof-state check, and shows the dialog (once per session) when needed.
 * Callers just kick this off; they do not orchestrate the UX.
 */
export const lazyInitializeAccountProofs: LazyAction<typeof lazyIndex.initializeAccountProofs, typeof initializationLazyModule> = new LazyAction(initializationLazyModule, m => m.initializeAccountProofs);

/**
 * App-tree host for the "Protect your account" dialog. Mount once (e.g. in AppModule); it only
 * imports and renders the dialog chunk once the store status is `Showing`.
 */
export const ProtectYourAccountDialogHost: LazyComponent<{}, unknown, void> = createLazyComponent(lazyModule, m => m.ProtectYourAccountDialogHost, undefined, undefined, {
    onlyImportIf: () => getProtectYourAccountDialogStore().status === "Showing",
});
